import { useCallback, useContext, useMemo } from 'react';
import countBy from 'lodash/countBy';

import { assertIsDefined } from '@/helpers/assertions';
import { sortAlphabetically } from '@/helpers/helpers';
import { sortAscEntitiesBy } from '@/providers/EntitiesListContextProvider/helpers';
import { EntitiesListContext } from '@/providers/EntitiesListContextProvider/EntitiesListContextProvider';
import { useGetUniqueEntitiesList } from '@/api/entityIds/entityIds.hooks';
import { useNewAuth } from '@/providers/AuthProvider/NewAuthProvider.hooks';
import {
  ICtxEntitiesList,
  ICtxEntity,
  IEntitiesManagedByTypes,
} from '@/providers/EntitiesListContextProvider/EntitiesListContext.types';
import { useNewFeature } from '@/providers/NewFeatureContextProvider/NewFeatureContext.hooks';

// CURRENTLY HIDDEN TYPES FOR MONITORING PURPOSES
const GAMBLING_TYPE = 'gambling';
const MISC_TYPE = 'misc';
const INFRASTRUCTURE_TYPE = 'infrastructure';

const useEntitiesByTypeManager = (entities: ICtxEntity[]): IEntitiesManagedByTypes => {
  //IMPORTANT!!! The list of exchanges may include exchanges with type as null!
  const listOfEntityTypes = useMemo(() => {
    if (!entities.length) return [];

    const availableTypes = Array.from(
      new Set(entities.map(entity => entity.type).filter((e): e is string => e !== null)),
    );

    return sortAlphabetically(availableTypes);
  }, [entities]);

  const listOfEntityTypesEnabledForUser = useMemo(() => {
    return listOfEntityTypes.filter(
      type => type !== GAMBLING_TYPE && type !== MISC_TYPE && type !== INFRASTRUCTURE_TYPE,
    );
  }, [listOfEntityTypes]);

  const sortedEntitiesByEnabledType = useMemo(() => {
    if (!entities.length || !listOfEntityTypesEnabledForUser.length) return [];

    const sortedBucket: ICtxEntity[] = [];

    listOfEntityTypesEnabledForUser.forEach(type => {
      entities.forEach(entity => {
        if (entity.type === type) {
          sortedBucket.push(entity);
        }
      });
    });

    return sortedBucket;
  }, [listOfEntityTypesEnabledForUser, entities]);

  const sortedAscEntitiesOfEnabledTypes = useMemo(() => {
    return sortAscEntitiesBy(sortedEntitiesByEnabledType, 'value');
  }, [sortedEntitiesByEnabledType]);

  const totalAvailableEntitiesNumberDict = useMemo(() => {
    return countBy(sortedEntitiesByEnabledType, e => e.type);
  }, [sortedEntitiesByEnabledType]);

  return {
    listOfEntityTypesEnabledForUser,
    sortedEntitiesListOfEnabledTypes: {
      byTypeAsc: sortedEntitiesByEnabledType,
      asc: sortedAscEntitiesOfEnabledTypes,
    },
    totalAvailableEntitiesNumberByType: totalAvailableEntitiesNumberDict,
  };
};

export const useEntitiesListManager = () => {
  const { isAuthenticated } = useNewAuth();
  const { isAlertingPermitted } = useNewFeature();

  const {
    fetchedEntitiesList,
    isLoading: isEntitiesListLoading,
    error: entitiesListError,
  } = useGetUniqueEntitiesList({ isAuthenticated });

  // ________________ENTITIES FOR USER________________

  const entities: ICtxEntitiesList = useMemo(() => {
    if (!fetchedEntitiesList) {
      return {
        all: [],
        rated: [],
        monitored: [],
        monitoredAndRated: [],
        alerting: [],
        monitoredAndAlerting: [],
        withWithdrawals: [],
      };
    }

    const mappedEntitiesList = fetchedEntitiesList.map(
      ({
        agioId,
        displayName,
        type,
        id,
        isRated,
        isMonitored,
        isAlerting,
        status,
        hasTransfers,
      }) => {
        return {
          value: agioId,
          label: displayName,
          type: type,
          id,
          isRated: isRated,
          isMonitored,
          isAlerting,
          status,
          hasTransfers,
        };
      },
    );

    const sortedEntities = sortAscEntitiesBy(mappedEntitiesList, 'value');
    const ratedEntities = sortedEntities.filter(entity => entity.isRated);

    const monitoredEntities = sortedEntities.filter(entity => entity.isMonitored);
    const monitoredAndRatedEntities = sortedEntities.filter(
      entity => entity.isMonitored || entity.isRated,
    );
    const monitoredAndAlertingEntities = sortedEntities.filter(
      entity => entity.isMonitored || entity.isAlerting,
    );
    const hasTransfersEntities = sortedEntities.filter(entity => entity.hasTransfers);

    const alertingEntities = sortedEntities.filter(entity => entity.isAlerting);

    return {
      all: sortedEntities,
      rated: ratedEntities,
      monitored: monitoredEntities,
      monitoredAndRated: monitoredAndRatedEntities,
      alerting: alertingEntities,
      monitoredAndAlerting: monitoredAndAlertingEntities,
      withWithdrawals: hasTransfersEntities,
    };
  }, [fetchedEntitiesList]);

  const monitoringAndAlertingEntitiesManagedByTypes = useEntitiesByTypeManager(
    isAlertingPermitted ? entities.monitoredAndAlerting : entities.monitored,
  );
  const monitoredAndRatedEntitiesManagedByTypes = useEntitiesByTypeManager(
    entities.monitoredAndRated,
  );

  const getEntityValueByDisplayName = useCallback(
    (displayName: string) => {
      if (entities.all.length === 0) return '';

      const requestedEntity = [...entities.all].find(entity => entity.label === displayName);

      if (requestedEntity) {
        return requestedEntity.value;
      }

      console.error("Didn't find entity by displayName: ", displayName);
      return '';
    },
    [entities.all],
  );

  const getDisplayNameByEntityValue = useCallback(
    (entityValue: string) => {
      if (entities.all.length === 0) {
        console.error("List of entities is empty and couldn't find entity by value: ", entityValue);
        return entityValue;
      }

      const requestedEntity = [...entities.all].find(entity => entity.value === entityValue);

      if (requestedEntity) {
        return requestedEntity.label;
      }

      !!entityValue.length && console.error("Didn't find entity by value: ", entityValue);

      return entityValue;
    },
    [entities.all],
  );

  const getStatusByEntityValue = useCallback(
    (entityValue: string) => {
      if (entities.all.length === 0) {
        console.error("List of entities is empty and couldn't find entity by value: ", entityValue);
        return null;
      }

      const requestedEntity = [...entities.all].find(entity => entity.value === entityValue);

      if (requestedEntity) {
        return requestedEntity.status;
      }

      !!entityValue.length && console.error("Didn't find entity by value: ", entityValue);

      return null;
    },
    [entities.all],
  );

  const checkIsEntityValueRequestedType = useCallback(
    (entityValue: string, type: string) => {
      if (entities.all.length === 0) {
        console.error(
          "List of entities is empty and couldn't find entity by value and compare to requested type: ",
          entityValue,
        );
        return false;
      }

      const requestedEntity = [...entities.all].find(entity => entity.value === entityValue);

      if (requestedEntity) {
        return requestedEntity.type === type;
      }

      !!entityValue.length && console.error("Didn't find entity by value: ", entityValue);

      return false;
    },
    [entities.all],
  );

  return {
    isEntitiesListLoading,
    entitiesListError,

    entities,

    monitoringAndAlertingEntitiesManagedByTypes,
    monitoredAndRatedEntitiesManagedByTypes,

    getEntityValueByDisplayName,
    getDisplayNameByEntityValue,
    getStatusByEntityValue,
    checkIsEntityValueRequestedType,
  };
};

export const useEntitiesList = () => {
  const {
    entities,
    isEntitiesListLoading,
    entitiesListError,
    monitoringAndAlertingEntitiesManagedByTypes,
    monitoredAndRatedEntitiesManagedByTypes,
    getEntityValueByDisplayName,
    getDisplayNameByEntityValue,
    getStatusByEntityValue,
    checkIsEntityValueRequestedType,
  } = useContext(EntitiesListContext);

  assertIsDefined(entities, '"IEntitiesListContext.entities has to be defined!"');
  assertIsDefined(
    isEntitiesListLoading,
    '"IEntitiesListContext.isEntitiesListLoading has to be defined!"',
  );
  assertIsDefined(
    monitoringAndAlertingEntitiesManagedByTypes,
    '"IEntitiesListContext.monitoringEntitiesManagedByTypes has to be defined!"',
  );
  assertIsDefined(
    monitoredAndRatedEntitiesManagedByTypes,
    '"IEntitiesListContext.monitoringAndAlertingEntitiesManagedByTypes has to be defined!"',
  );
  assertIsDefined(
    getEntityValueByDisplayName,
    '"IEntitiesListContext.getEntityValueByDisplayName has to be defined!"',
  );
  assertIsDefined(
    getDisplayNameByEntityValue,
    '"IEntitiesListContext.getDisplayNameByEntityValue has to be defined!"',
  );
  assertIsDefined(
    getStatusByEntityValue,
    '"IEntitiesListContext.getStatusByEntityValue has to be defined!"',
  );
  assertIsDefined(
    checkIsEntityValueRequestedType,
    '"IEntitiesListContext.checkIsEntityValueRequestedType has to be defined!"',
  );

  return {
    entities,
    isEntitiesListLoading,
    entitiesListError,
    monitoringAndAlertingEntitiesManagedByTypes,
    monitoredAndRatedEntitiesManagedByTypes,
    getEntityValueByDisplayName,
    getDisplayNameByEntityValue,
    getStatusByEntityValue,
    checkIsEntityValueRequestedType,
  };
};
